import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';

import { env } from '@/env';
import { useAppSelector } from '@/store/hooks';
import { useFeaturedAvatar } from '@/hooks/useFeaturedAvatar';
import { usePuoCreareAvatar } from '@/hooks/usePuoCreareAvatar';
import { useMovimentoRidotto } from '@/hooks/useMovimentoRidotto';
import { useIsVisible } from '@/hooks/useIsVisible';
import { useInVista } from '@/hooks/useInVista';
import { ChromaKeyVideo } from '@/components/avatars/ChromaKeyVideo';
import { DualStreamVideo } from '@/components/avatars/DualStreamVideo';
import { supportsVp9Alpha } from '@/lib/videoAlphaSupport';
import { cn } from '@/lib/cn';

/**
 * The player's QAvatar, floating over the app banner — bottom-left, head free
 * to rise above the banner's top edge.
 *
 * This used to be the Hub's alone. It now lives in `<AppBanner />`, so the
 * avatar holds the same spot on every screen, and it is the ONLY avatar: the
 * legacy Laravel picture circle is gone.
 *
 * Renders nothing when the player has an avatar that cannot float (opaque, or a
 * mesh still in flight) — the banner stays exactly as it was.
 *
 * Renders a tappable placeholder ONLY when the section is on AND the server has
 * answered "no featured avatar" (`featured === null`). It is NOT the only entrance
 * to `/avatars` (others: the Avatars row in Settings, `SettingsList.tsx`; the tap on
 * the featured avatar below; the emotes page, `AvatarEmotes.tsx`; and the v3 Hub
 * Banner tile, `v3/chrome/HubBanner.tsx`) but it is the one a player with NO avatar
 * meets first, so it cannot vanish for the players who need it most; inside
 * Telegram nobody can type a URL. `undefined` (loading, error,
 * section off) is "I don't know", not "none" — no placeholder, or a player whose
 * avatar has not arrived yet would be invited to create a second one.
 *
 * `striscia`: la testata fuori dal Profilo e' una striscia alta 56 px (layout-tma-orcb,
 * I-303; reference KB `services/qlash-telegram-app/ui/2026-10-02-testata-striscia-reference.md`).
 * La resa resta la stessa — la FIGURA intera, in proporzione (I-307: niente tondo, niente
 * ritaglio), video, immagine o segnaposto — solo piu' piccola: alta 72 px, appoggiata al
 * fondo della striscia a 14 px dal suo bordo sinistro. Sporge SOLO verso l'alto, nei 16 px
 * di margine della testata, e mai sotto, nel contenuto (la vecchia sporgeva di 16-18 px e
 * copriva il titolo delle Notizie). Non segue la compressione: da compressa la striscia ha
 * la stessa altezza. Senza `striscia` e' il banner del Profilo, invariato (ADR-0069).
 * L'animazione occupa ESATTAMENTE la scatola della figura statica (I-420, vedi
 * `LARGHEZZA_FIGURA_STRISCIA`).
 */
/**
 * La larghezza della figura nella striscia, per formato dell'avatar (`aspectRatio`): alta 72 px, la
 * figura statica e' un'immagine `h-full w-auto`, cioe' larga quanto il suo formato (72 o 54 px).
 * La clip ANIMATA deve stare in quella stessa scatola (I-420, banner-avatar-orc), e la sua scatola
 * va scritta: la tela del video e' `w-full` dentro un contenitore assoluto senza larghezza, e senza
 * una larghezza esplicita la sceglie il MOTORE. Misurato 05/10 con la prima clip vera di staging
 * (512×512): WebKit, come la WebView di iOS, la allargava a tutta la striscia (306/346/376 px a
 * 320/360/390) e col vecchio `translate-x-[-30%]` la figura finiva 25-39 px a destra, sopra il nome;
 * Chromium la teneva a 72 e la stessa traslazione la portava 21,6 px a sinistra, fuori dallo schermo.
 * Una clip in un formato diverso dall'immagine resta contenuta e appoggiata al fondo.
 * Misura: `tools/tma-e2e/scenarios/misura-avatar-animato.mjs --atteso ancorata`.
 */
const LARGHEZZA_FIGURA_STRISCIA: Record<'1:1' | '3:4', string> = { '1:1': 'w-[72px]', '3:4': 'w-[54px]' };

export function BannerAvatar({ striscia = false }: { striscia?: boolean } = {}) {
  const { t } = useTranslation('general');
  const { t: ta } = useTranslation('avatars');
  const { featured, clip, showVideo, showImage } = useFeaturedAvatar();
  // Compact banner has NO row above it, so the headroom the head normally rises
  // into is gone (the shell clips at the top). Shrink the avatar in compact so
  // the head stays below the clip line while the feet keep the same anchor.
  const compact = useAppSelector((s) => s.settings.bannerCompact);
  // I-416: col flag Studio acceso (solo staging) chi non e' `administrator` non crea: il segnaposto non gli promette
  // la creazione, gli dice di scegliere. Stessa destinazione. Flag spento: «Create your avatar», come oggi (I-270).
  const puoCreare = usePuoCreareAvatar();
  // Il banner come la bolla (`BollaEmote`): col movimento ridotto niente video, la figura ferma; a scheda nascosta o figura fuori vista il
  // video sta fermo. Gli hook stanno prima di ogni `return` (regola degli hook).
  const ridotto = useMovimentoRidotto();
  const schedaVisibile = useIsVisible();
  const [figura, setFigura] = useState<HTMLDivElement | null>(null);
  const inVista = useInVista(figura);
  const fermo = !schedaVisibile || !inVista;
  // Il webm legacy e' un <video> a vista (autoPlay): la stessa regola di `fermo`, pause() / play() col catch. L'elemento si passa come
  // STATO, perche' compare solo quando la clip e' arrivata; montato a scheda nascosta parte gia' fermo.
  const [webm, setWebm] = useState<HTMLVideoElement | null>(null);
  useEffect(() => {
    if (!webm) return;
    if (fermo) webm.pause();
    else {
      try {
        void webm.play()?.catch(() => {});
      } catch {
        /* play() assente o negato: resta fermo */
      }
    }
  }, [webm, fermo]);
  // Nella striscia la clip poggia sul fondo della scatola, come la figura statica (`object-bottom`).
  const piedi = striscia ? 'object-bottom' : undefined;

  // Movimento ridotto: la figura e' l'immagine scontornata dell'avatar (`featured.imageUrl`), SOLO se il server dice `hasAlpha` (e' la
  // stessa che il banner mostra gia' senza clip). Con un'immagine opaca non ce n'e' una sicura: niente figura, come per l'avatar opaco.
  const animato = showVideo && !ridotto;
  const figuraFerma = (showImage || (showVideo && ridotto)) && !!featured?.imageUrl && !!featured.hasAlpha;
  if (!animato && !figuraFerma) {
    // The flag guard is redundant with `skip` in `useFeaturedAvatar` (off ⇒ the
    // query never runs ⇒ `featured` is never null) — kept so the rule reads here.
    if (!env.featureAvatarsEnabled || featured !== null) return null;
    // `/avatars` and not `/avatars/new`: "no featured avatar" is not "no avatars"
    // (the featured route can answer null to someone who owns some), and the
    // gallery already carries the create CTA in its empty state — for who can create (I-416): the others
    // get «pick your avatar», because for them the gallery has no entry to creation.
    return (
      <Link
        to="/avatars"
        aria-label={
          puoCreare
            ? t('avatars.banner_create', { defaultValue: 'Create your avatar' })
            : t('avatars.row_hint_pick', { defaultValue: 'Pick your avatar' })
        }
        className={cn(
          'absolute z-[1] flex aspect-square items-center justify-center rounded-full',
          'border-2 border-dashed border-brand/60 bg-bg-secondary/60 text-brand',
          'transition-transform active:scale-95',
          // Nella striscia: 44 px (la zona tattile minima), centrato sull'orma della figura.
          striscia
            ? 'bottom-[6px] left-[28px] h-11'
            : cn('bottom-[8%] left-[10%]', compact ? 'h-[75%]' : 'h-[85%]'),
        )}
      >
        <Sparkles aria-hidden className="h-1/2 w-1/2" />
      </Link>
    );
  }

  return (
    <>
      {/* Tap target — confined to the avatar's footprint on the LEFT of the
          banner, so taps near the right-side game selector never open
          /avatars. The visual is pointer-events-none, so this bounded link is
          the only thing that catches a tap; `peer` drives its press feedback.
          It must stay BEFORE the visual: `peer-*` only reaches later siblings. */}
      <Link
        to="/avatars"
        aria-label={ta('banner.open')}
        className={cn(
          'peer absolute z-[1]',
          // Nella striscia l'orma e' la figura stessa, 72×72: sopra i 44 px del varco.
          striscia
            ? 'bottom-0 left-[14px] h-[72px] w-[72px]'
            : cn('bottom-[-25%] left-0 w-[46%]', compact ? 'h-[125%]' : 'h-[160%]'),
        )}
      />
      {/* Decorative — never captures taps.
          Pushed DOWN and LEFT off the banner's bottom-left corner: anchored at
          bottom-0 the head (h-180%) reached ~66px above the banner and the
          screen edge clipped it. bottom-[-25%] drops it ~20px so the whole head
          fits the headroom the top row leaves; left-[-4%] pulls it ~13px left —
          out of the banner, but inside the banner's own 16px padding, so it
          never reaches the screen edge. Compact has no top row, so the height
          drops to h-140% to keep the head under the clip line. */}
      <div
        ref={setFigura}
        aria-hidden
        className={cn(
          'pointer-events-none absolute z-[1]',
          striscia
            ? 'bottom-0 left-[14px] h-[72px]'
            : cn('bottom-[-25%] left-[-4%]', compact ? 'h-[140%]' : 'h-[180%]'),
        )}
      >
        {animato ? (
          // Video output is 1:1 (768x768 from fal.ai providers) — no aspect
          // wrapper, which would distort the canvas. The canvas's intrinsic
          // width/height (set per-frame from video.videoWidth/Height) carries
          // the ratio; h-full + w-auto lets the browser preserve it.
          //
          // translate-x-[-30%] shifts the canvas leftward by 30% of its own
          //   width, pulling the subject flush with the left edge the static
          //   PNG path uses (the source frame has empty margins around it).
          // translate-y-[-4%] consumes the source frame's sub-feet padding so
          //   the feet land on the banner's bottom rather than below it.
          // Nella striscia niente di tutto questo: la scatola e' quella della figura
          //   statica, scritta (`LARGHEZZA_FIGURA_STRISCIA`), e la clip la riempie.
          <div
            className={cn(
              'drop-shadow-[0_8px_16px_rgba(0,0,0,0.4)] transition-transform peer-active:scale-95',
              striscia
                ? cn('h-full', LARGHEZZA_FIGURA_STRISCIA[featured!.aspectRatio] ?? LARGHEZZA_FIGURA_STRISCIA['1:1'])
                : 'h-full w-auto translate-x-[-30%] translate-y-[-4%]',
            )}
          >
            {clip!.matteRgbUrl && clip!.matteAlphaUrl ? (
              // H.264 dual-stream — universal. Edge decomposite removes the
              // backdrop tint from semi-transparent edges. Legacy clips carry
              // chromaKeyHex (magenta); new ones don't (veed pre-mattes RGB to
              // black), so black is the default.
              <DualStreamVideo
                key={`dual-${clip!.clipId}`}
                className={piedi}
                rgbUrl={clip!.matteRgbUrl}
                alphaUrl={clip!.matteAlphaUrl}
                poster={clip!.posterUrl ?? featured!.imageUrl}
                fermo={fermo}
                knownBgHex={
                  // Veo always mattes without magenta preprocessing, so a stale
                  // chromaKeyHex would mis-decomposite the border pixels and
                  // amplify scene-colour residue into a halo. Force #000000 for
                  // Veo. See AvatarDetail effectiveBgHexFor() for the twin.
                  clip!.provider === 'veo31-fal' || clip!.provider === 'veo31-fast-fal'
                    ? '#000000'
                    : (clip!.chromaKeyHex ?? '#000000')
                }
              />
            ) : clip!.webmAlphaUrl && supportsVp9Alpha() ? (
              // Legacy WebM VP9-alpha — Chromium only.
              <video
                key={`webm-${clip!.clipId}`}
                ref={setWebm}
                src={clip!.webmAlphaUrl}
                poster={clip!.posterUrl ?? featured!.imageUrl}
                autoPlay
                loop
                muted
                playsInline
                className={cn('h-full w-full object-contain', piedi)}
              />
            ) : (
              <ChromaKeyVideo
                // key={clipId} forces a clean remount when the active clip
                // changes: React would otherwise reuse the same <video>, which
                // doesn't auto-reload on src changes — the old MP4 keeps playing.
                key={clip!.clipId}
                className={piedi}
                paused={fermo}
                src={clip!.videoUrl!}
                poster={clip!.posterUrl ?? featured!.imageUrl}
                keyColor={clip!.chromaKeyHex ?? '#FF00FF'}
              />
            )}
          </div>
        ) : (
          <img
            src={featured!.imageUrl}
            alt=""
            className="h-full w-auto object-contain object-bottom drop-shadow-[0_8px_16px_rgba(0,0,0,0.4)] transition-transform peer-active:scale-95"
          />
        )}
      </div>
    </>
  );
}
